/**
 * @fileoverview Form controls for scheduling and duration — single row layout.
 */
import { useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import TimeInputControl from '@/features/tasks/components/forms/fields/TimeInputControl';
import { TIME_SLOT_MINS } from '@/config/constants';
import { translate as t } from '@/i18n/useT';
import { formatHours } from '@/components/ui/formatHours';
import { parseDurationInput } from '@/utils/timeUtils';
import {
  fieldLabelSx,
  formGroupSx,
  nativeInputSx,
  planningSectionSx,
} from '@/features/tasks/components/forms/taskFormStyles';
import { TaskDateTimeFieldErrors } from '@/features/tasks/components/forms/fields/TaskDateTimeFieldErrors';
import { TaskDateTimeRecurrenceOptions } from '@/features/tasks/components/forms/fields/TaskDateTimeRecurrenceOptions';

const TaskDateTimeFields = ({
  task,
  errors,
  isRollup,
  calculatedDuration,
  isRecurrenceEnabled,
  setIsRecurrenceEnabled,
  recurrenceInterval,
  setRecurrenceInterval,
  recurrenceFrequency,
  setRecurrenceFrequency,
  handleChange,
  handleTimeChange,
  handleDurationChange,
}) => {
  const displayDuration = (minutes) =>
    minutes || minutes === 0 ? formatHours(minutes / 60) : '';

  const [durationText, setDurationText] = useState(
    displayDuration(isRollup ? calculatedDuration : task.duration),
  );

  useEffect(() => {
    setDurationText(displayDuration(isRollup ? calculatedDuration : task.duration));
  }, [isRollup, calculatedDuration, task.duration]);

  const commitDuration = () => {
    const parsed = parseDurationInput(durationText);
    handleChange({ target: { name: 'duration', value: parsed ?? '' } });
  };

  if (isRollup) return null;

  return (
    <Box sx={planningSectionSx}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            ...formGroupSx,
            flex: 1,
            minWidth: { xs: '100%', sm: 120 },
          }}
        >
          <Box component="label" htmlFor="date" sx={fieldLabelSx}>
            {t('taskdatetimefields.date', 'Date')}
          </Box>
          <Box
            component="input"
            type="date"
            id="date"
            name="date"
            value={task.date || ''}
            onChange={handleChange}
            disabled={isRollup}
            readOnly={isRollup}
            sx={nativeInputSx}
          />
        </Box>

        <Box sx={{ ...formGroupSx, flex: 1, minWidth: 100 }}>
          <TimeInputControl
            label={t('taskdatetimefields.time', 'Time')}
            id="time-input"
            value={task.time || ''}
            onChange={(e) =>
              handleChange({ target: { name: 'time', value: e.target.value } })
            }
            onIncrement={() => handleTimeChange(TIME_SLOT_MINS)}
            onDecrement={() => handleTimeChange(-TIME_SLOT_MINS)}
            disabled={isRollup}
          />
        </Box>

        <Box sx={{ ...formGroupSx, flex: 1, minWidth: 120 }}>
          <TimeInputControl
            label={t('taskdatetimefields.duration', 'Duration')}
            id="duration-input"
            value={durationText}
            onChange={(e) => setDurationText(e.target.value)}
            onBlur={commitDuration}
            onIncrement={() => handleDurationChange(TIME_SLOT_MINS)}
            onDecrement={() => handleDurationChange(-TIME_SLOT_MINS)}
            disabled={isRollup}
          />
        </Box>

        <Box sx={{ flexShrink: 0, alignSelf: 'flex-end' }}>
          <IconButton
            onClick={() => setIsRecurrenceEnabled(!isRecurrenceEnabled)}
            aria-label={t(
              'taskdatetimefields.toggleRecurrence',
              'toggle recurrence',
            )}
            color={isRecurrenceEnabled ? 'primary' : 'default'}
          >
            <RepeatIcon />
          </IconButton>
        </Box>
      </Box>
      <TaskDateTimeFieldErrors errors={errors} />
      <TaskDateTimeRecurrenceOptions
        isRecurrenceEnabled={isRecurrenceEnabled}
        recurrenceInterval={recurrenceInterval}
        setRecurrenceInterval={setRecurrenceInterval}
        recurrenceFrequency={recurrenceFrequency}
        setRecurrenceFrequency={setRecurrenceFrequency}
      />
    </Box>
  );
};

export default TaskDateTimeFields;
